import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Use Cases
import { getBreadcrumbItemWithVisibleChildren } from './breadcrumbUseCase';
import { findMenuItemById } from '../menu/menuNavigationUseCase';

// Utils
import { normalizeUrl } from '../../utils/url';

/**
 * Breadcrumb sub-menu popover state: visible items, the item whose popover is open, and navigation from its links.
 * @param {ApiManagerBreadcrumb[] | null | undefined} items - Breadcrumb items
 * @returns {{
 *  visibleItems: ApiManagerBreadcrumb[],
 *  openItemId: string | null,
 *  toggleItem: (itemId: string) => void,
 *  closePopover: () => void,
 *  navigateToItemId: (itemId: string) => void,
 * }}
 */
const useBreadcrumb = (items) => {
  const navigate = useNavigate();
  const [openItemId, setOpenItemId] = useState(/** @type {string | null} */ (null));

  const visibleItems = useMemo(() => (items ?? []).map(getBreadcrumbItemWithVisibleChildren), [items]);
  const openItem = useMemo(
    () => visibleItems.find(({ id }) => id === openItemId) ?? null,
    [visibleItems, openItemId],
  );

  /**
   * @param {string} itemId
   */
  const toggleItem = (itemId) => {
    setOpenItemId((current) => (current === itemId ? null : itemId));
  };

  const closePopover = () => {
    setOpenItemId(null);
  };

  /**
   * Navigate to a sub-menu entry of the open item.
   * @param {string} itemId
   */
  const navigateToItemId = (itemId) => {
    const item = findMenuItemById(openItem?.items, itemId);
    if (!item?.path) return;
    navigate(normalizeUrl(item.path));
    setOpenItemId(null);
  };

  return {
    visibleItems,
    // Items change on navigation: an id that no longer exists closes the popover
    openItemId: openItem ? openItemId : null,
    toggleItem,
    closePopover,
    navigateToItemId,
  };
};

export default useBreadcrumb;
